import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Terminal, RefreshCw, Search, ChevronDown, Loader2, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, cardCls, btnPrimary,
  Toggle, PageShell, PageLoading, EmptyState, IdPicker, useChannelsAndRoles,
} from '@/lib/pageKit';

const isCustomized = (c) => !c.enabled || (c.allowed_role_ids || []).length > 0 || (c.allowed_channel_ids || []).length > 0;

function CommandRow({ cmd, roles, channels, onSave, onReset }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(cmd);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(cmd); }, [cmd]);

  const dirty = draft.enabled !== cmd.enabled
    || JSON.stringify(draft.allowed_role_ids || []) !== JSON.stringify(cmd.allowed_role_ids || [])
    || JSON.stringify(draft.allowed_channel_ids || []) !== JSON.stringify(cmd.allowed_channel_ids || []);

  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const isHelp = cmd.name === 'help';

  return (
    <div>
      <div className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-hover)] transition-colors">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
          <div className="min-w-0">
            <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope] flex items-center gap-2">
              <span className="font-mono">/{cmd.name}</span>
              {isCustomized(cmd) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#5865F2]/15 border border-[#5865F2]/25 text-[#5865F2]">Customized</span>
              )}
            </p>
            <p className="text-[var(--text-muted)] text-xs font-[Manrope] truncate">{cmd.description}</p>
          </div>
        </button>
        <Toggle
          checked={draft.enabled}
          disabled={isHelp}
          onChange={v => {
            setDraft(d => ({ ...d, enabled: v }));
            if (!open) run(() => onSave({ ...draft, enabled: v }));
          }}
        />
      </div>
      {open && (
        <div className="px-5 pb-4 pt-1 space-y-4 bg-[var(--bg-elevated)]/40">
          {isHelp && <p className="text-[var(--text-muted)] text-xs font-[Manrope]">The help command cannot be disabled.</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <IdPicker label="Allowed roles" items={roles} value={draft.allowed_role_ids}
              onChange={v => setDraft(d => ({ ...d, allowed_role_ids: v }))} placeholder="Everyone" />
            <IdPicker label="Allowed channels" items={channels} prefix="#" value={draft.allowed_channel_ids}
              onChange={v => setDraft(d => ({ ...d, allowed_channel_ids: v }))} placeholder="Everywhere" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" disabled={busy || !isCustomized(cmd)} onClick={() => run(() => onReset(cmd.name))}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-[Manrope] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] disabled:opacity-40 transition-colors">
              <RotateCcw size={13} /> Reset
            </button>
            <button type="button" disabled={busy || !dirty} onClick={() => run(() => onSave(draft))} className={btnPrimary}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CommandPermissions() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const { channels, roles } = useChannelsAndRoles(isGlobal ? null : selectedGuildId);

  const load = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/api/command-permissions/${selectedGuildId}`, authHeader());
      setCommands(r.data.commands || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load commands');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId, isGlobal]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const patch = (name, next) => setCommands(cs => cs.map(c => (c.name === name ? { ...c, ...next } : c)));

  const save = async (c) => {
    try {
      const body = {
        command: c.name, enabled: c.name === 'help' ? true : !!c.enabled,
        allowed_role_ids: c.allowed_role_ids || [], allowed_channel_ids: c.allowed_channel_ids || [],
      };
      await axios.put(`${API}/api/command-permissions/${selectedGuildId}`, body, authHeader());
      patch(c.name, body);
      toast.success(`/${c.name} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save command');
      load();
    }
  };

  const reset = async (name) => {
    try {
      await axios.delete(`${API}/api/command-permissions/${selectedGuildId}/${encodeURIComponent(name)}`, authHeader());
      patch(name, { enabled: true, allowed_role_ids: [], allowed_channel_ids: [] });
      toast.success(`/${name} reset to defaults`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset command');
    }
  };

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = {};
    commands
      .filter(c => !q || c.name.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q))
      .forEach(c => { (map[c.category || 'Other'] ||= []).push(c); });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [commands, query]);

  if (loading) return <PageLoading />;

  return (
    <PageShell
      icon={Terminal} title="Command Permissions" subtitle="Enable, disable or restrict bot commands by role and channel"
      testId="commands-main" isGlobal={isGlobal} maxW="max-w-4xl"
      actions={
        <button onClick={load} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search commands…" className={`${inputCls} pl-10`} />
      </div>

      {groups.length === 0 ? (
        <EmptyState icon={Terminal} title={query ? 'No commands match your search' : 'No commands available'} />
      ) : groups.map(([cat, list]) => (
        <div key={cat}>
          <h2 className="text-[var(--text-muted)] text-xs font-semibold uppercase tracking-widest font-[Manrope] mb-2 px-1">{cat}</h2>
          <div className={`${cardCls} divide-y divide-[var(--border)] overflow-hidden`}>
            {list.map(c => (
              <CommandRow key={c.name} cmd={c} roles={roles} channels={channels} onSave={save} onReset={reset} />
            ))}
          </div>
        </div>
      ))}
    </PageShell>
  );
}
